import { useEffect, useState } from 'react';
import { Button, Input, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../shared/components';
import { EditIcon, TrashIcon } from '../../../shared/icons';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { HomeLocation, HomeLocationStatus } from '../../../shared/services/turnoGeo/types';

const repo = getTurnoGeoRepository();

const STATUS_TONE: Record<HomeLocationStatus, 'warning' | 'success' | 'critical'> = { PENDIENTE: 'warning', APROBADA: 'success', RECHAZADA: 'critical' };
const STATUS_LABEL: Record<HomeLocationStatus, string> = { PENDIENTE: 'Pendiente', APROBADA: 'Aprobada', RECHAZADA: 'Rechazada' };

/**
 * Configuración > Ubicaciones registradas (casa y oficina). Cada persona
 * registra las suyas desde "Marcar > Registrar mi ubicación" (IP que ve el
 * servidor, o solo GPS si estaba con datos móviles); aquí el administrador las
 * aprueba o rechaza, las corrige o las elimina. Solo una ubicación APROBADA
 * verifica un marcaje. Si el parámetro de aprobación está apagado, se aprueban solas.
 */
export function HomeLocationsTab() {
  const toast = useToast();
  const [rows, setRows] = useState<HomeLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<HomeLocation | null>(null);
  const [form, setForm] = useState({ publicIpAddress: '', gpsLatitude: '', gpsLongitude: '', gpsRadiusMeters: '150' });
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<HomeLocation | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listHomeLocations().then((r) => {
      setRows(r);
      setIsLoading(false);
    });
  };
  useEffect(load, []);

  const review = async (r: HomeLocation, status: 'APROBADA' | 'RECHAZADA') => {
    try {
      await repo.reviewHomeLocation(r.id, status);
      const what = r.locationKind === 'OFICINA' ? 'Oficina' : 'Casa';
      toast.show(status === 'APROBADA' ? `${what} de ${r.userFullName} aprobada.` : `${what} de ${r.userFullName} rechazada.`);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar la revisión.', 'error');
    }
  };

  const openEdit = (r: HomeLocation) => {
    setForm({
      publicIpAddress: r.publicIpAddress ?? '',
      gpsLatitude: r.gpsLatitude != null ? String(r.gpsLatitude) : '',
      gpsLongitude: r.gpsLongitude != null ? String(r.gpsLongitude) : '',
      gpsRadiusMeters: String(r.gpsRadiusMeters),
    });
    setEditing(r);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setIsSaving(true);
    try {
      await repo.updateHomeLocation(editing.id, {
        publicIpAddress: form.publicIpAddress.trim(),
        gpsLatitude: form.gpsLatitude.trim() ? Number(form.gpsLatitude) : null,
        gpsLongitude: form.gpsLongitude.trim() ? Number(form.gpsLongitude) : null,
        gpsRadiusMeters: Number(form.gpsRadiusMeters) || 150,
      });
      toast.show('Casa actualizada.');
      setEditing(null);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const columns: Column<HomeLocation>[] = [
    { key: 'user', filterValue: (r) => r.userFullName, header: 'Persona', render: (r) => r.userFullName, sortable: true, sortValue: (r) => r.userFullName },
    { key: 'kind', filterValue: (r) => r.locationKind, header: 'Tipo', render: (r) => <Badge tone={r.locationKind === 'OFICINA' ? 'info' : 'neutral'}>{r.locationKind === 'OFICINA' ? 'Oficina' : 'Casa'}</Badge> },
    { key: 'ip', header: 'IP / red', render: (r) => (r.publicIpAddress ? <span className="break-all">{r.publicIpAddress}</span> : <span className="text-ink-400">Solo GPS</span>) },
    {
      key: 'gps',
      header: 'GPS',
      render: (r) => (r.gpsLatitude != null ? `${r.gpsLatitude.toFixed(5)}, ${r.gpsLongitude!.toFixed(5)} (${r.gpsRadiusMeters} m)` : '—'),
    },
    { key: 'status', filterValue: (r) => STATUS_LABEL[r.status], header: 'Estado', render: (r) => <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge> },
    {
      key: 'review',
      header: 'Revisión',
      render: (r) => (
        <div className="flex gap-2">
          {r.status !== 'APROBADA' && (
            <Button size="md" variant="secondary" onClick={() => review(r, 'APROBADA')}>
              Aprobar
            </Button>
          )}
          {r.status !== 'RECHAZADA' && (
            <Button size="md" variant="outline" onClick={() => review(r, 'RECHAZADA')}>
              Rechazar
            </Button>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-400">
        Cada persona registra su casa u oficina desde <strong>Marcar &gt; Registrar mi ubicación</strong>, estando en ese lugar. Solo una ubicación <strong>aprobada</strong> verifica un marcaje. Si el parámetro
        "Exigir aprobación del administrador al registrar casa u oficina" está apagado, se aprueban solas.
      </p>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="📍" title="Sin ubicaciones registradas" description="Cuando un empleado registre su casa u oficina desde Marcar, aparecerá aquí." />
      ) : (
        <DataTable excel fixedKey="user" columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} emptyTitle="Sin ubicaciones" />
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`${editing?.locationKind === 'OFICINA' ? 'Oficina' : 'Casa'} de ${editing?.userFullName ?? ''}`}>
        <div className="space-y-3">
          <Input
            label="IP o red (vacío = solo GPS)"
            hint='IPv4 exacta o prefijo IPv6, ej. 2800:484:dd97:9a00::/64'
            value={form.publicIpAddress}
            onChange={(e) => setForm({ ...form, publicIpAddress: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Latitud" value={form.gpsLatitude} onChange={(e) => setForm({ ...form, gpsLatitude: e.target.value })} />
            <Input label="Longitud" value={form.gpsLongitude} onChange={(e) => setForm({ ...form, gpsLongitude: e.target.value })} />
          </div>
          <Input label="Radio del geocerco (metros)" type="number" value={form.gpsRadiusMeters} onChange={(e) => setForm({ ...form, gpsRadiusMeters: e.target.value })} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={() => setEditing(null)}>Cancelar</Button>
            <Button fullWidth onClick={saveEdit} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar ubicación"
        description={`¿Eliminar la ${deleting?.locationKind === 'OFICINA' ? 'oficina' : 'casa'} registrada de ${deleting?.userFullName}? Sus marcajes dejarán de verificarse con ella hasta que registre una nueva.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteHomeLocation(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
